/**
 * Udeploy constants will be overitten during deployment using
 * wildcards $foo$. Each enviorment will have own unique values
 * in Udeploy configuration.
 *
 * For local development sets defaults using regex.
 */

import { paramNum } from '@upr-web-primer/app-utils';
import { IEnvironment } from './environment.type';

// this function strips everything for wildcard to allow local development
export function param(str: string): string {
  return str.replace(/\$.*\$/g, '');
}

function paramBool(str: string): boolean | undefined {
  const fixedStr = param(str);
  if (fixedStr === '') {
    return undefined;
  }
  if (fixedStr === 'true') {
    return true;
  } else {
    return false;
  }
}

export const udeploy: IEnvironment = {
  accountAppPath: param('$accountAppPath$'),
  adobeAutosearchUrl: param('$adobeAutosearchUrl$'),
  adobeBodyScript: param('$adobeBodyScript$'),
  adobeHeaderScript: param('$adobeHeaderScript$'),
  adobeMbox: param('$adobeMbox$'),
  adobeTargetDisabled:
    (param('$adobe.target.disabled$') || '').toLowerCase().trim() === 'true',
  appTheme: param('$appTheme$'),
  baseRedirectBypass: paramBool('$baseRedirectBypass$') || false,
  bingSiteVerificationKey: param('$bingSiteVerificationKey$'),
  cardinalScriptUrl: param('$cardinalScriptUrl$'),
  checkSaveTimeProperty: Boolean(param('$checkSaveTimeProperty$')),
  CHOICE_A_SECTION: param('$CHOICE_A_SECTION$'),
  CHOICE_B_SECTION: param('$CHOICE_B_SECTION$'),
  DINING_RESERVATION: param('$DINING_RESERVATION$'),
  commerceApi: param('$commerceApi$'),
  commerceApiUrl: param('$commerceApiUrl$'),
  commerceClientId: param('$commerceClientId$'),
  commerceClientKeyID: param('$commerceClientKeyID$'),
  COMPLETE_AP: param('$COMPLETE_AP$'),
  config: {
    createAccount: {
      checkboxes: param('$config.createAccount.checkboxes$'),
      links: param('$config.createAccount.links$'),
    },
    dashboard: {
      isNavigate: Boolean(param('$config.dashboard.isNavigate$')),
      matchIconByUrl: Boolean(param('$config.dashboard.matchIconByUrl$')),
      showBuyTicketsLink: Boolean(
        param('$config.dashboard.showBuyTicketsLink$')
      ),
      showDiningReservationLink: Boolean(
        param('$config.dashboard.showDiningReservationLink$')
      ),
      showLinkTicketsLink: Boolean(
        param('$config.dashboard.showLinkTicketsLink$')
      ),
      showLinkTicketType: Boolean(
        param('$config.dashboard.showLinkTicketType$')
      ),
      showTicketContent: Boolean(param('$config.dashboard.showTicketContent$')),
      showTicketDetailsCta: Boolean(
        param('$config.dashboard.showTicketDetailsCta$')
      ),
    },
    deletingUSHAccount: Boolean(param('$config.deletingUSHAccount$')),
    displayFirstNameFirst: Boolean(param('$config.displayFirstNameFirst$')),
    isV1_1FeatureEnabled: Boolean(param('$config.isV1_1FeatureEnabled$')),
    profileTPMDetails: {
      otherMembers: Boolean(param('$profileTPMDetails.otherMembers$')),
    },
    showContentCallout: Boolean(param('$showContentCallout$')),
    showEntitlementsInfo: Boolean(param('$showEntitlementsInfo$')),
    showReEnterPassword: Boolean(param('$showReEnterPassword$')),
    showShareQr: Boolean(param('$showShareQr$')),
    viewEntitlements: Boolean(param('$viewEntitlements$')),
  },
  contentApiBaseUrl: param('$contentApiBaseUrl$'),
  gds_footer: {
    content_id: param('$gds_footer.content_id$'),
  },
  tridion: {
    publication: param('$tridion.publication$'),
  },
  contentIds: {
    GDS_GLOBAL_NAV_MEGA_NAV_CONTENT_ID: param(
      '$contentIds.GDS_GLOBAL_NAV_MEGA_NAV_CONTENT_ID$'
    ),
    GDS_GLOBAL_NAV_UTILITY_BAR_CONTENT_ID: param(
      '$contentIds.GDS_GLOBAL_NAV_UTILITY_BAR_CONTENT_ID$'
    ),
  },
  contentIdTokenUrl: param('$contentIdTokenUrl$'),
  contentLoginClientID: param('$contentLoginClientID$'),
  contentLoginClientKeyID: param('$contentLoginClientKeyID$'),
  contentOidcTokenApi: Boolean(param('$contentOidcTokenApi$')),
  cookieSubDomain: param('$cookieSubDomain$'),
  coveo: {
    accessKeyID: param('$coveo.accessKeyID$'),
    crossOrigin: param('$coveo.crossOrigin$'),
    css: param('$coveo.css$'),
    cssHash: param('$coveo.cssHash$'),
    enabled: Boolean(param('$coveo.enabled$')),
    organizationId: param('$coveo.organizationId$'),
    scriptLocale: param('$coveo.scriptLocale$'),
    scriptLocaleHash: param('$coveo.scriptLocaleHash$'),
    scriptSearchHash: param('$coveo.scriptSearchHash$'),
    scriptSearchUrl: param('$coveo.scriptSearchUrl$'),
    scriptTemplateHash: param('$coveo.scriptTemplateHash$'),
    scriptTemplateUrl: param('$coveo.scriptTemplateUrl$'),
  },
  crewToolsApi: param('$crewToolsApi$'),
  defaultCountry: param('$default.country$'),
  defaultCurrency: param('$default.currency$'),
  defaultLanguage: param('$default.language$'),
  defaultTheme: param('$defaultTheme$'),
  digitalDataAdblockerEnabled:
    (param('$digitalDataAdblockerEnabled$') || '').toLowerCase().trim() ===
    'true',
  digitalDataAngularVersion: param('$digitalDataAngularVersion$'),
  digitalDataAppVersion: param('$digitalDataAppVersion$'),
  digitalDataDestination: param('$digitalDataDestination$'),
  digitalDataEnabled:
    (param('$digitalDataEnabled$') || '').toLowerCase().trim() === 'true',
  digitalDataEnvironment: param('$digitalDataEnvironment$'),
  digitalDataFlashVersion: param('$digitalDataFlashVersion$'),
  digitalDataSegment: param('$digitalDataSegment$'),
  digitalDataSite: param('$digitalDataSite$'),
  digitalDataTridionVersion: param('$digitalDataTridionVersion$'),
  diningReservationsAppPath: param('$diningReservationsAppPath$'),
  denaliUrl: param('$denaliUrl$'),
  deleteAccountConfirmationFlow: Boolean(
    param('$deleteAccountConfirmationFlow$')
  ),
  deleteRedirectURL: param('$deleteRedirectURL$'),
  displayDateByTimezone: Boolean(param('$displayDateByTimezone$')),
  entitlementBaseUrl: param('$entitlementBaseUrl$'),
  entitlementDataUrl: param('$entitlementDataUrl$'),
  facebookLikeUrl: param('$facebookLikeUrl$'),
  faviconUrl: param('$faviconUrl$'),
  fetchTravelPartyUrl: param('$fetchTravelPartyUrl$'),
  fontCssUrl: param('$fontCssUrl$'),
  geo: {
    apiUrl: param('$geo.apiUrl$'),
    disabled: paramBool('$geo.disabled$') || false,
    location: {
      akamai: paramBool('$geo.location.akamai$') || false,
    },
    service: param('$geo.service$'),
    region: {
      primary: param('$geo.region.primary$'),
    },
  },
  getTicketsLink: param('$getTicketsLink$'),
  globalNavigationEnabled:
    (param('$globalNavigationEnabled$') || '').toLowerCase().trim() === 'true',
  googleCalendarApiKey: param('$googleCalendarApiKey$'),
  googleSiteVerificationKey: param('$googleSiteVerificationKey$'),
  grecaptchaApiUrl: param('$grecaptchaApiUrl$'),
  grecaptchaSiteKey: param('$grecaptchaSiteKey$'),
  grecaptchaEnabled:
    (param('$grecaptchaEnabled$') || '').toLowerCase().trim() === 'true',
  grecaptchaTestResponse: param('$grecaptchaTestResponse$'),
  gtmId: param('$gtmID$'),
  hideScanIconDesktop: Boolean(param('$hideScanIconDesktop$')),
  hmacKey: param('$hmacKey$'),
  iconCssUrl: param('$iconCssUrl$'),
  inquiryToken: param('$inquiryToken$'),
  isMMDDYYYYFormat: Boolean(param('$isMMDDYYYYFormat$')),
  isRToasterTagEnabled: Boolean(param('$isRToasterTagEnabled$')),
  layoutHeader: param('$layoutHeader$'),
  linkEntitlement: param('$linkEntitlement$'),
  mobileServicesApi: {
    baseUrl: param('$mobileServicesApi.baseUrl$'),
    key: param('$mobileServicesApi.key$'),
    token: param('$mobileServicesApi.token$'),
    city: param('$mobileServicesApi.city$'),
    cityCode: param('$mobileServicesApi.cityCode$'),
    showTimesEndpoint: param('$mobileServicesApi.showTimesEndpoint$'),
    venueEndpoint: param('$mobileServicesApi.venueEndpoint$'),
  },
  oauthAppFlowPath: param('$oauthAppFlowPath$'),
  oauthAppPasswordFlowPath: param('$oauthAppPasswordFlowPath$'),
  oauthAppSourceId: param('$oauthAppSourceId$'),
  oauthBaseUrl: param('$oauthBaseUrl$'),
  oauthClientKeyID: param('$oauthClientKeyID$'),
  oidc: {
    app_flow_path: param('$oidc.app_flow_path$'),
    base_url: param('$oidc.base_url$'),
    enabled: (param('$oidc.enabled$') || '').toLowerCase().trim() === 'true',
    end_session: param('$oidc.end_session$'),
    client_id: param('$oidc.client_id$'),
    client_secret: param('$oidc.client_secret$'),
    node_api_base_url: param('$oidc.node_api_base_url$'),
    password_flow_path: param('$oidc.password_flow_path$'),
    redirect_enabled:
      (param('$oidc.redirect_enabled$') || '').toLowerCase().trim() === 'true',
    redirect_url: param('$oidc.redirect_url$'),
    refresh_expires_in_seconds: paramNum('$oidc.refresh_expires_in_seconds$'),
  },
  oidcInquiryClientId: param('$oidcInquiryClientId$'),
  oidcInquiryClientKeyID: param('$oidcInquiryClientKeyID$'),
  oneTrustCrossorigin: param('$oneTrustCrossorigin$'),
  oneTrustDomainScript: param('$oneTrustDomainScript$'),
  oneTrustEnabled: Boolean(param('$oneTrustEnabled$')),
  oneTrustHash: param('$oneTrustHash$'),
  oneTrustModalScript: param('$oneTrustModalScript$'),
  oneTrustPrivacyGeo: param('$oneTrustPrivacyGeo$'),
  oneTrustPrivacyIds: param('$oneTrustPrivacyIds$'),
  oneTrustPrivacyLSPA: param('$oneTrustPrivacyLSPA$'),
  oneTrustPrivacyScript: param('$oneTrustPrivacyScript$'),
  pageLabels: param('$pageLabels$'),
  pageLabelsUrl: param('$pageLabelsUrl$'),
  parkInfo: {
    abbreviatedMonthDayYearFormat: param(
      '$parkInfo.abbreviatedMonthDayYearFormat$'
    ),
    MonthDayYearFormat: param('$parkInfo.MonthDayYearFormat$'),
    MonthDayYearWithWeekday: param('$parkInfo.MonthDayYearWithWeekday$'),
    TimeZone: param('$parkInfo.TimeZone$'),
  },
  qrCodeAPIParam: param('$qrCodeAPIParam$'),
  qrCodeAPIUrl: param('$qrCodeAPIUrl$'),
  qrCodeDomainUrl: param('$qrCodeDomainUrl$'),
  resortAreaCode: param('$default.resortareacode$'),
  resortName: param('$resort.name$'),
  rToasterTagId: param('$rToasterTagId$'),
  sap_cart: {
    commerce_client_id: param('$sap_cart.commerce_client_id$'),
    commerce_client_secret_id: param('$sap_cart.commerce_client_secret_id$'),
    commerce_client_creds: param('$sap_cart.commerce_client_creds$'),
    is_cart_enabled: Boolean(param('$sap_cart.is_cart_enabled$')),
    occ_base_url: param('$sap_cart.occ_base_url$'),
    base_site: param('$sap_cart.base_site$'),
  },
  sendParamsTravelParty: Boolean(param('$sendParamsTravelParty$')),
  seoDescription: param('$seoDescription$'),
  seoFacebookSiteName: param('$seoFacebookSiteName$'),
  seoImage: param('$seoImage$'),
  seoTitle: param('$seoTitle$'),
  seoTwitterSiteName: param('$seoTwitterSiteName$'),
  shareQrCodeUrl: param('$shareQrCodeUrl$'),
  shareQrEntitlementsAvailable: Boolean(
    param('$shareQrEntitlementsAvailable$')
  ),
  showAlphanumericLinkError: Boolean(param('$showAlphanumericLinkError$')),
  showCommPrefCard: Boolean(param('$showCommPrefCard$')),
  showCreditCardDetails: Boolean(param('$showCreditCardDetails$')),
  showCreditCardExpDate: Boolean(param('$showCreditCardExpDate$')),
  showdeliveryCost: Boolean(param('$showdeliveryCost$')),
  showEdit: Boolean(param('$showEdit$')),
  showGalaxyTickets: Boolean(param('$showGalaxyTickets$')),
  showICELinkInCoupon: Boolean(param('$showICELinkInCoupon$')),
  showLogoutCard: Boolean(param('$showLogoutCard$')),
  showPasswordCard: Boolean(param('$showPasswordCard$')),
  showPaymentCard: Boolean(param('$showPaymentCard$')),
  showPhoneCard: Boolean(param('$showPhoneCard$')),
  showProfileBirthday: Boolean(param('$showProfileBirthday$')),
  showProfileGender: Boolean(param('$showProfileGender$')),
  showProfileUpdates: Boolean(param('$showProfileUpdates$')),
  showTax: Boolean(param('$showTax$')),
  showTaxLabel: Boolean(param('$showTaxLabel$')),
  siteName: param('$siteName$'),
  sorryPagePath: param('$sorryPagePath$'),
  SourceIdKey: {
    en: {
      WebUpdatePassword: param('$SourceIdKey.en.WebUpdatePassword$'),
      WebPaymentCard: param('$SourceIdKey.en.WebPaymentCard$'),
      WebAccountCreation: param('$SourceIdKey.en.WebAccountCreation$'),
      WebAccountDelete: param('$SourceIdKey.en.WebAccountDelete$'),
      WebCreateorEditProfileTravelParty: param(
        '$SourceIdKey.en.WebCreateorEditProfileTravelParty$'
      ),
      WebEditAddress: param('$SourceIdKey.en.WebEditAddress$'),
      WebEditCommunications: param('$SourceIdKey.en.WebEditCommunications$'),
      WebEditContactInfo: param('$SourceIdKey.en.WebEditContactInfo$'),
      WebEditPersonalInfo: param('$SourceIdKey.en.WebEditPersonalInfo$'),
      WebLinkEntitlements: param('$SourceIdKey.en.WebLinkEntitlements$'),
    },
    ja: {
      WebAccountDelete: param('$SourceIdKey.ja.WebAccountDelete$'),
      WebUpdatePassword: param('$SourceIdKey.ja.WebUpdatePassword$'),
      WebPaymentCard: param('$SourceIdKey.ja.WebPaymentCard$'),
      WebAccountCreation: param('$SourceIdKey.ja.WebAccountCreation$'),
      WebCreateorEditProfileTravelParty: param(
        '$SourceIdKey.ja.WebCreateorEditProfileTravelParty$'
      ),
      WebEditAddress: param('$SourceIdKey.ja.WebEditAddress$'),
      WebEditCommunications: param('$SourceIdKey.ja.WebEditCommunications$'),
      WebEditContactInfo: param('$SourceIdKey.ja.WebEditContactInfo$'),
      WebEditPersonalInfo: param('$SourceIdKey.ja.WebEditPersonalInfo$'),
      WebLinkEntitlements: param('$SourceIdKey.ja.WebLinkEntitlements$'),
    },
  },
  specialOffers: Boolean(param('$specialOffers$')),
  subTotalTaxIncluded: Boolean(param('$subTotalTaxIncluded$')),
  taxCodes: {
    display: Boolean(param('$taxCodes.display$')),
    NV_LIVE_ENT: param('$taxCodes.NV_LIVE_ENT$'),
    SALES_TAX: param('$taxCodes.SALES_TAX$'),
  },
  timeZone: param('$default.timeZone$'),
  tridionBaseUrl: param('$tridionBaseUrl$'),
  tridionSite: param('$tridionSite$'),
  tripDetailLink: param('$tripDetailLink$'),
  uniWebServiceApiKey: param('$uniWebServiceApiKey$'),
  uprvUrl: param('$uprvUrl$'),
  upsellPackages: param('$upsellPackages$'),
  wcsStoreId: param('$wcsStoreId$'),
  weatherApi: param('$weatherApi$'),
  webAppPath: param('$webAppPath$'),
  webstoreAppPath: param('$webstoreAppPath$'),
  webstoreJunction: param('$webstoreJunction$'),
  wtsHostBaseUrl: param('$wtsHostBaseUrl$'),
  baseSite: param('$baseSite$'),
};
